import {
  ovl,
  unsafeHTML,
  type DirectiveResult,
  type SYSAction,
  type SYSActions,
  type SYSDialog,
  type SYSLongRunningProcess,
  type SYSScreen,
  type SYSState,
  type SYSTableDefIds,
  type TemplateResult,
} from "../.."
import { T } from "../../global/globals"
import {
  HookTableRowFnResultExecution,
  HookTableRowFnResultExecution_Type,
  HookTableServerBeforeMergeRow,
  HookTableServerData,
  HookTableServerData_Type,
  ServerData_ChangedType,
  TableRowFnProcessFeedback_Type,
  TableRowFnProcessFeedbackFull_Type,
  TableRowFnProcessRawFeedbackSummaryCount_Type,
} from "../../global/hooks"
import { DialogOk, DialogOkCancel } from "../dialog/dialogapi"
import { DialogType } from "../dialog/sysdialogbase"
import type { SYSFormState } from "../forms/actions"
import { ScreenCurrentState } from "../screenapi"
import type { SnackType } from "../snack/snack"
import { SnackAdd, SnackTrackedAdd, SnackTrackedRemove } from "../snackapi"
import { _TableRefreshDataFromServerHelper } from "../table/actions"
import {
  _AddRowToDisplayedArray,
  _DoRebuildAndFixPage,
  _DoServerDataConversionSyncAndBuildUpRebuildAndChangedInfo,
  _GetTableDialogTitle,
  _MergeRowHookParamsType,
  DoRefreshLookups,
} from "../table/helpers"
import { OvlSysColumn } from "../table/table"
import {
  SYSTableDef,
  TableGet,
  TableRemoveRowFromData,
} from "../table/tableapi"
import { ProcessEnd, ProcessIsRunning } from "./processapi"

export type LongRunningProcessState = {
  isRunning: boolean
  id: string
  lockedFormType?: { formType: string; formId: string }[]
  lockedDialog?: SYSDialog
  lockedDialogInstance?: any
  lockedScreen?: SYSScreen
  lockedScreenInstance?: string
  processType?: string
  instanceId?: string
  tableFn?: {
    // lockedTableKeys contains the locked keys
    // means when a result returns it will be unlocked by removed from here
    // originally selectedKeys
    lockedTableKeys?: {}
    selectedKeys?: {}
    fnName?: string
    fnId?: string
    tableDefId?: SYSTableDefIds

    rowKey?: string
  }
  ts?: number
  hasTrackedSnack: boolean
  refCounter: number
  currentFeedback: { [key: string]: TableRowFnProcessFeedbackFull_Type }
}

export type ProcessSnackType = {
  type?: SnackType
  fireAndForgetSnackDuration?: number
  text?: string
  alternativeTranslationKey?: string
  customTextReplacements?: string[]
  cancelable?: boolean
}

type ProcessLockDef = {
  formState?: SYSFormState | SYSFormState[]
  dialog?: SYSDialog
  dialogInstance?: any
  screen?: SYSScreen
  screenInstance?: string
}

export const _LongRunningProcessStart: SYSAction<
  {
    processType: SYSLongRunningProcess
    instance?: string
    snack?: ProcessSnackType
    lock?: ProcessLockDef
    isRefresh?: boolean
    // provide the locked tableKeys as Object wo we can quickly can check the keys
    tableFn?: {
      rowKey: string
      lockedTableKeys?: {}
      fnName?: string
      fnId: string
      tableDefId: SYSTableDefIds
    }
  },
  _ProcessReturnType
> = (value, { global }) => {
  let state = global.getState()
  let actions = global.getActions()
  return LongRunningProcessStartHelper(
    actions,
    state,
    value.processType,
    value.instance,
    value.snack,
    value.lock,
    value.isRefresh,
    value.tableFn
  )
}

export type _ProcessReturnType = {
  currentFeedback: { [key: string]: TableRowFnProcessFeedback_Type }
  trackedSnackId: string
  tableFn: {
    // lockedTableKeys contains the locked keys
    // means when a result returns it will be unlocked by removed from here
    // originally selectedKeys
    lockedTableKeys?: {}
    selectedKeys?: {}
    fnName?: string
    fnId?: string
    tableDefId?: SYSTableDefIds
    rowKey?: string
  }

  process: SYSLongRunningProcess
  instance: string
  cancelled?: boolean
  // we can deliver a success but still have a optional warning
  setSuccess: (
    id: string,
    text?: string | TemplateResult | DirectiveResult,
    code?: string,
    cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
  ) => void
  setInformation: (
    id: string,
    text?: string | TemplateResult | DirectiveResult,
    code?: string,
    cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
  ) => void

  setWarning: (
    id: string,
    text?: string | TemplateResult | DirectiveResult,
    code?: string,
    cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
  ) => void
  setError: (
    id: string,
    text?: string | TemplateResult | DirectiveResult,
    code?: string,
    cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
  ) => void
  setFromResult: (result: TableRowFnProcessFeedbackFull_Type) => void
  showSummary: (
    summaryType: "Snack" | "DialogIfMultiple" | "DialogAlways",
    positionText?: string
  ) => void
  stop: () => void
}

const LongRunningProcessStartHelper = (
  actions: SYSActions,
  state: SYSState,
  processType: SYSLongRunningProcess,
  instance?: string,
  snack?: ProcessSnackType,
  lock?: ProcessLockDef,
  isRefresh?: boolean,
  tableFn?: {
    rowKey: string
    lockedTableKeys?: {}
    fnName?: string
    fnId: string
    tableDefId: SYSTableDefIds
  }
): _ProcessReturnType => {
  let allProcessesState = state.ovl.longRunningProcessTracker

  // delete processstate from non running instances that has no feedback and are older than x minutes
  // they are tracked and recreated
  // to avoid unecessary object creation remove them
  let ts = new Date().getTime()
  Object.keys(allProcessesState).forEach((process) => {
    let instance = allProcessesState[process].instance
    let instancesToDelete = Object.keys(instance)
      .filter(
        (instanceKey) =>
          // some old SYSModal instance SYSModal as well type should not be affected
          instanceKey !== process &&
          // not running
          !instance[instanceKey].isRunning &&
          // not started for 5 minutes
          instance[instanceKey].ts - ts > 1000 * 60 * 5 &&
          // if there are feedbacks don't delete
          Object.keys(instance[instanceKey].feedback).length === 0
      )
      .map((k) => k)
    instancesToDelete.forEach((f) => {
      delete allProcessesState[process].instance[f]
    })
  })

  if (!instance) {
    instance = processType
  }
  let processMainState = allProcessesState[processType]

  if (!processMainState) {
    return
  }
  if (!snack && !processMainState.snackFixedByAppDef) {
    processMainState.snack = undefined
  }

  if (isRefresh !== undefined) {
    processMainState.isRefresh = isRefresh
  }

  if (!snack) {
    let snackReplacements = snack?.customTextReplacements
    snack = processMainState.snack
    if (snackReplacements) {
      snack.customTextReplacements = snackReplacements
    }
  } else if (snack.type === "none") {
    snack = undefined
  }
  if (snack && !snack.type) {
    snack.type = "Information"
  }

  let lockScreen = processMainState.lockScreen

  if (!lock?.screen && lockScreen) {
    let currentScreenState = ScreenCurrentState()
    let currentScreen = currentScreenState.screen
    let currentScreenInstance = currentScreenState.instance
    if (!lock) {
      lock = { screen: currentScreen, screenInstance: currentScreenInstance }
    } else {
      lock.screen = currentScreen
      lock.screenInstance = currentScreenInstance
    }
  }
  let hasSnack = !!snack
  let hasTrackedSnack =
    hasSnack && snack.fireAndForgetSnackDuration === undefined
  let id = `${processType}_${instance}`
  let instanceState =
    state.ovl.longRunningProcessTracker[processType]["instance"][instance]

  //_RenderTracking.forceTracking = true
  if (!instanceState) {
    state.ovl.longRunningProcessTracker[processType]["instance"][instance] = {
      isRunning: true,
      id,
      hasTrackedSnack,
      refCounter: 0,

      currentFeedback: {},

      tableFn: {},
      processType,
      instanceId: instance,
    }
  }

  //_RenderTracking.forceTracking = false
  instanceState =
    state.ovl.longRunningProcessTracker[processType]["instance"][instance]
  if (tableFn) {
    instanceState.tableFn.fnId = tableFn.fnId
    instanceState.tableFn.fnName = tableFn.fnName
    instanceState.tableFn.lockedTableKeys = tableFn.lockedTableKeys
    instanceState.tableFn.selectedKeys = JSON.parse(
      JSON.stringify(tableFn.lockedTableKeys)
    )
    instanceState.tableFn.tableDefId = tableFn.tableDefId
    instanceState.tableFn.rowKey = tableFn.rowKey
  }
  instanceState.processType = processType
  instanceState.instanceId = instance
  instanceState.ts = new Date().getTime()

  instanceState.isRunning = true
  // currentFeedback should reset on each start
  instanceState.currentFeedback = {}
  instanceState.id = id
  if (!instanceState.hasTrackedSnack) {
    instanceState.hasTrackedSnack = hasTrackedSnack
  }
  if (isNaN(instanceState.refCounter)) {
    instanceState.refCounter = 0
  }
  // don't delete feedback... we would like to see errors and warnings as long as the id was not executed successfully or somehow dismissed (todo by the user)
  // for process summary errors count etc we can use the currentFeedback
  //instanceState.feedback = {}
  instanceState.refCounter++

  let lockedScreen
  let lockedFormState
  let lockedDialog

  if (lock) {
    if (Array.isArray(lock.formState)) {
      lockedFormState = lock.formState.map((m) => {
        m.locked = true
        return {
          formType: m.formType,
          formId: m.formId,
        }
      })
    } else if (lock.formState) {
      lock.formState.locked = true
      lockedFormState = [
        {
          formType: (<SYSFormState>lock.formState).formType,
          formId: (<SYSFormState>lock.formState).formId,
        },
      ]
    }
    lockedDialog = lock.dialog
    let lockedDialogInstance = lock.dialogInstance

    if (lockedDialog) {
      actions.ovl.dialog._DialogSetLock({
        dialog: lockedDialog,
        locked: true,
        instance: lockedDialogInstance,
      })
    }

    if (lock.screen) {
      actions.ovl.screen._ScreenSetLock({
        screen: lock.screen,
        locked: true,
        instance: lock.screenInstance,
      })
    }
    // let instanceState =
    //   state.ovl.longRunningProcessTracker[processType]["instance"][instance]

    instanceState.lockedFormType = lockedFormState
    instanceState.lockedDialog = lockedDialog
    instanceState.lockedDialogInstance = lockedDialogInstance
    instanceState.lockedScreen = lock.screen
    instanceState.lockedScreenInstance = lock.screenInstance
  }
  const setOnRelatedProcessesHelper = (
    id: string,
    type: "error" | "warning" | "success" | "information" | "remove",
    text: string | TemplateResult | DirectiveResult,
    code: string,
    cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type,
    defId?: SYSTableDefIds
  ) => {
    if (cnt === undefined) {
      cnt = "cis"
    }
    let ts = new Date().getTime()

    // todo: is it necessary to still check for Multi and Instance and all HasFeatureThatStartsWith
    // because now it gets looped twice and rowfeedback ovlsyststae can be set once...
    // because fnFeedback on the process is just for the summary right?
    // and this we should get out from process which started all (instanceState)

    if (instanceState.tableFn?.lockedTableKeys) {
      let tb = TableGet(instanceState.tableFn.tableDefId)

      let ovlSysState = tb.rows[id]?.OvlSysState as OvlSysColumn

      if (type !== "remove") {
        if (ovlSysState) {
          //  save feedback to sysstaterow
          // (improves perf for rendering row)
          ovlSysState.fnFeedback[instanceState.tableFn.fnId] = {
            type,
            text,
            code,
            cnt,
            ts,
            defId,
          }
          let lvl = parseInt(tb.def.options.feedback.level.split(":")[0])
          if (
            lvl !== 0 &&
            (lvl >= 4 ||
              (lvl === 3 && type !== "success") ||
              (lvl === 2 && type !== "success" && type !== "warning") ||
              (lvl === 1 && type === "error"))
          ) {
            ovlSysState.lastFeedbackTS = ts
            // we need to set the rebuild needsRefresh here if we sort by feedback
            Object.keys(tb.dataDef.tableDef)
              .filter(
                (f) =>
                  (<SYSTableDef>tb.dataDef.tableDef[f]).initialised &&
                  !(<SYSTableDef>tb.dataDef.tableDef[f]).uiState.rebuild
                    .needsRefresh &&
                  (<SYSTableDef>tb.dataDef.tableDef[f]).uiState.sort?.byFeedback
              )
              .forEach((tdk) => {
                let td: SYSTableDef = tb.dataDef.tableDef[tdk]
                td.uiState.rebuild.needsRefresh = true
              })
          }
        }
      } else {
        if (ovlSysState?.fnFeedback) {
          delete ovlSysState.fnFeedback[instanceState.tableFn.fnId]
        }
      }
      instanceState.currentFeedback[id] = { type, text, code, ts, cnt }
      delete instanceState.tableFn.lockedTableKeys[id]
    }
  }
  let executionHookFn
  let hookTableRowFnResultExecution: HookTableRowFnResultExecution_Type
  if (tableFn) {
    let tb = TableGet(tableFn.tableDefId)
    let def = tb.def
    executionHookFn =
      actions.app.features[`${def.feature}`]?.hooks[
        HookTableRowFnResultExecution
      ]

    if (executionHookFn) {
      let data = tb.dataDef
      hookTableRowFnResultExecution = {
        currentResult: undefined,
        mode: "start",
        executionContextToSet: {
          custom: {},
          doNotExecuteSystemAfterResultHandler: false,
          doNotSetFeedbackOnProcess: false,
        },
        fnKey: tableFn.fnId,
        fnName: tableFn.fnName,
        data,
        def,
        currentFeedback: instanceState.currentFeedback,
      }
    }
  }
  let res: _ProcessReturnType = {
    currentFeedback: instanceState.currentFeedback,
    instance,
    process: processType,
    trackedSnackId: undefined,
    tableFn: instanceState.tableFn,
    setSuccess: (
      id: string,
      text?: string,
      code?: string,
      cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
    ) => {
      setOnRelatedProcessesHelper(
        id,
        "success",
        text,
        code,
        cnt,
        instanceState.tableFn.tableDefId
      )
    },
    setInformation: (
      id: string,
      text?: string,
      code?: string,
      cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
    ) => {
      setOnRelatedProcessesHelper(
        id,
        "information",
        text,
        code,
        cnt,
        instanceState.tableFn.tableDefId
      )
    },
    setWarning: (
      id: string,
      text?: string,
      code?: string,
      cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
    ) => {
      setOnRelatedProcessesHelper(
        id,
        "warning",
        text,
        code,
        cnt,
        instanceState.tableFn.tableDefId
      )
    },
    setError: (
      id: string,
      text?: string,
      code?: string,
      cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
    ) => {
      setOnRelatedProcessesHelper(
        id,
        "error",
        text,
        code,
        cnt,
        instanceState.tableFn.tableDefId
      )
    },
    setFromResult: async (result: TableRowFnProcessFeedbackFull_Type) => {
      if (executionHookFn) {
        hookTableRowFnResultExecution.currentResult = result
        hookTableRowFnResultExecution.mode = "beforeResultHandler"
        await executionHookFn(hookTableRowFnResultExecution)
      }
      if (
        !executionHookFn ||
        !hookTableRowFnResultExecution.executionContextToSet
          .doNotExecuteSystemAfterResultHandler
      ) {
        if (result.fieldsRes) {
          let tb = TableGet(instanceState.tableFn.tableDefId)
          DoRefreshLookups(actions, tb.def, [result.fieldsRes], undefined)
          // Object.keys(result.fieldsRes).forEach((k) => {
          // there could be any result.id sent back from backend (eg. activities externalsend notifications changes as well other activities from that contact)
          let changed: ServerData_ChangedType
          let returnedKeys: Map<string, ServerData_ChangedType> = new Map<
            string,
            ServerData_ChangedType
          >()
          if (tb.rows[result.id] !== undefined) {
            let mergeRowHookParams: _MergeRowHookParamsType
            let mergeFn =
              actions.app.features[tb.def.feature]?.hooks?.[
                HookTableServerBeforeMergeRow
              ]
            if (mergeFn) {
              mergeRowHookParams = {
                defId: tb.def.id,
                fn: mergeFn,
                tableData: tb.dataDef,
              }
            }
            changed =
              _DoServerDataConversionSyncAndBuildUpRebuildAndChangedInfo(
                result.id,
                tb.dataDef.clientConversion,
                result.fieldsRes,

                tb.rows[result.id],
                tb.def.uiState.rebuild,
                false,
                mergeRowHookParams,
                tb.dataDef.checkForOtherModification
              )
          }
          //})
          // we also need to call refreshRow hook
          if (Object.keys(changed.columns).length > 0) {
            returnedKeys.set(result.id, changed)
            _DoRebuildAndFixPage(tb.def)
            let tableServerDataFnName = HookTableServerData
            let tableServerDataFn =
              actions.app.features[`${tb.def.feature}`]?.hooks[
                tableServerDataFnName
              ]
            if (tableServerDataFn) {
              await tableServerDataFn(<HookTableServerData_Type>{
                more: false,
                ext: {},
                type: "RefreshRow",
                tableData: tb.dataDef,
                tableDefId: tb.def.id,
                returnedKeys,
              })
            }
          }
        }

        if (result.tableRefreshData) {
          let tb = TableGet(instanceState.tableFn.tableDefId)
          let res = {
            data: { data: [result.tableRefreshData] },
          }
          await _TableRefreshDataFromServerHelper(
            res,
            tb.def,
            tb.dataDef,
            "Refresh"
          )

          if (!tb.rows[result.id]) {
            let key = tb.dataDef.index[result.id]
            if (tb.def.features.autoRebuild !== "AfterModifyOrDeleteOrAdd") {
              // if there is no autorebuild add the new ones according top/bottom setting
              _AddRowToDisplayedArray(tb.def, key)
            }
            // was a new one so set rowKey now
            result.id = key
            // and also make sure there is a process
            // this will create a multi process but that should be fine
            let processType =
              tb.def.options.customRowActions[result.fnId].process
            let instanceId = `${tb.def.id}_OvlSep_${result.fnId}_OvlSep_Multi`
            // trick to create the basic state if it not exists
            ProcessIsRunning(<SYSLongRunningProcess>processType, instanceId)
            // add the relevant tableFn props to state
            let processInstanceState = <LongRunningProcessState>(
              state.ovl.longRunningProcessTracker[processType].instance[
                instanceId
              ]
            )
            if (!processInstanceState.tableFn.fnId) {
              processInstanceState.tableFn.fnId = result.fnId
              processInstanceState.tableFn.tableDefId = tb.def.id
              processInstanceState.tableFn.rowKey = undefined
            }
          }
        }
        //@ts-ignore
        if (result.type === "remove") {
          TableRemoveRowFromData(
            instanceState.tableFn.tableDefId,
            result.id,
            {
              isFirst: false,
              isLast: false,
            } /* isFirst, isLast is jsut a workaround because TableRemoveRow doesnt auto rebuild) */
          )
        }
      }
      if (executionHookFn) {
        hookTableRowFnResultExecution.mode = "afterResultHandler"
        await executionHookFn(hookTableRowFnResultExecution)
      }
      if (
        !executionHookFn ||
        !hookTableRowFnResultExecution.executionContextToSet
          .doNotSetFeedbackOnProcess
      ) {
        setOnRelatedProcessesHelper(
          result.id,
          result.type,
          result.text,
          result.code,
          result.cnt,
          instanceState.tableFn.tableDefId
        )
      }
    },
    showSummary: (
      summaryType: "Snack" | "DialogIfMultiple" | "DialogAlways",
      positionText: string
    ) => {
      let selectedKeys = instanceState.tableFn?.selectedKeys
      if (!selectedKeys) {
        return
      }
      let selectedKeysCount = Object.keys(selectedKeys).length
      let fromAPI = selectedKeysCount < 1
      let isSingle = selectedKeysCount === 1
      // let isMultiple = selectedKeysCount > 1

      let feedback = instanceState.currentFeedback

      // Check for missing keys (Cancelled) and add synthetic feedback
      if (!fromAPI && res.cancelled) {
        let missingKeys = Object.keys(selectedKeys).filter((k) => !feedback[k])

        if (missingKeys.length > 0) {
          let cancelText = T("SYSRowFnCancelledByUser")
          missingKeys.forEach((k) => {
            setOnRelatedProcessesHelper(
              k,
              "warning",
              cancelText,
              "UserCancelled",
              "cis",
              instanceState.tableFn.tableDefId
            )
          })
        }
      }

      let successKeys = Object.keys(feedback).filter(
        (k) => feedback[k].type === "success" && feedback[k].cnt === "cis"
      )

      let warningKeys = Object.keys(feedback).filter(
        (k) => feedback[k].type === "warning" && feedback[k].cnt === "cis"
      )
      let errorKeys = Object.keys(feedback).filter(
        (k) => feedback[k].type === "error" && feedback[k].cnt === "cis"
      )
      let feedbackCount =
        warningKeys.length + errorKeys.length + successKeys.length
      if (feedbackCount < 2) {
        // directly snack out error/warning or display simple success
        let hint: any = ""
        let snackType: SnackType = "Success"
        let snackDuration = 4000

        if (errorKeys.length > 0) {
          snackType = "Error"
          if (
            feedback[errorKeys[0]].code &&
            "OvlServerOffline,OvlResourceForbidden,".indexOf(
              feedback[errorKeys[0]].code
            ) > -1
          ) {
            // we don't need to spit out "Offline" or "ResourceForbidden" - Snack. This was already done by the underlying fetch impl resp. not necessary
            return
          }
          hint = feedback[errorKeys[0]].text
          snackDuration = 8000
        } else if (warningKeys.length > 0) {
          snackType = "Warning"
          hint = feedback[warningKeys[0]].text
          snackDuration = 8000
        }
        if (hint) {
          hint = `: ${hint}`
        }
        if (summaryType === "Snack" || summaryType === "DialogIfMultiple") {
          SnackAdd(
            T("SYSTableRowFnProcessRowFnSummarySingle", [
              instanceState.tableFn.fnName,
              hint,
            ]),
            snackType,
            snackDuration
          )
        } else {
          let tb = TableGet(instanceState.tableFn.tableDefId)
          let title
          if (fromAPI) {
            title = instanceState.tableFn.fnName
          } else {
            title = _GetTableDialogTitle(
              tb.def,
              instanceState.tableFn.fnName,
              selectedKeysCount,
              tb.dataDef.data,
              undefined,
              true
            )
          }
          DialogOk(
            T("SYSTableRowFnProcessRowFnSummarySingle", [
              instanceState.tableFn.fnName,
              hint,
            ]),
            {
              type: <DialogType>snackType.toLowerCase(),
              title,
              instance: instanceState.instanceId,
            }
          )
        }
        return
      }

      let snackType: SnackType = "Success"

      if (warningKeys.length || errorKeys.length) {
        snackType = "Warning"
      }
      let showDetails = snackType === "Warning" || feedbackCount > 1

      let summarySubType = "WithCount"
      if (fromAPI || (isSingle && showDetails)) {
        summarySubType = "WithDetails"
      } else if (showDetails) {
        summarySubType = "WithCountAndDetails"
      }

      let summaryTranslationKey = `${processType}RowFnSummary${summarySubType}`
      if (!state.ovl.language.translations[summaryTranslationKey]) {
        summaryTranslationKey = `SYSTableRowFnProcessRowFnSummary${summarySubType}`
      }

      if (summaryType === "Snack") {
        let snackText = unsafeHTML(
          T(summaryTranslationKey, [
            instanceState.tableFn.fnName,
            positionText,
            selectedKeysCount,
            successKeys.length,
            warningKeys.length,
            errorKeys.length,
          ])
        )
        SnackAdd(snackText, snackType, 15000)
      } else if (summaryType.startsWith("Dialog")) {
        let tb = TableGet(instanceState.tableFn.tableDefId)
        let title
        if (fromAPI) {
          title = instanceState.tableFn.fnName
        } else {
          title = _GetTableDialogTitle(
            tb.def,
            instanceState.tableFn.fnName,
            selectedKeysCount,
            tb.dataDef.data,
            undefined,
            true
          )
        }
        var errorKeysHint = errorKeys.length.toString()
        // check for OvlUnhandled from backend (if there was an unhandled exception whilst rowfn loop -> should not happen! always try catch for rowfn on backend!)
        // but thanks to this code we have an indication. in case of this error we need a fix in the backend. also check audit for details
        let ovlUnhandled = Object.keys(instanceState.currentFeedback).some(
          (k) =>
            instanceState.currentFeedback[k].text &&
            instanceState.currentFeedback[k].text
              .toString()
              .indexOf("OvlUnhandled") > -1
        )
        if (ovlUnhandled) {
          errorKeysHint = "OvlUnhandled"
        }
        DialogOk(
          unsafeHTML(
            T(summaryTranslationKey, [
              instanceState.tableFn.fnName,
              positionText,
              selectedKeysCount,
              successKeys.length,
              warningKeys.length,
              errorKeysHint,
            ])
          ),
          {
            type: <DialogType>snackType.toLowerCase(),
            title,
            instance: instanceState.instanceId,
          }
        )
      }
    },
    stop: () => {
      if (executionHookFn) {
        hookTableRowFnResultExecution.mode = "end"
        hookTableRowFnResultExecution.currentResult = undefined

        executionHookFn(hookTableRowFnResultExecution)
      }
      ProcessEnd(processType, instance)
    },
  }
  //console.log("Start Process: ", processType, instance)
  if (hasSnack) {
    let text = snack.text
    if (!text) {
      let replacements = [T(instance, undefined, "Key")]
      if (
        snack.customTextReplacements &&
        snack.customTextReplacements.length > 0
      ) {
        replacements = snack.customTextReplacements
      }

      let alternativeTranslationCode = snack.alternativeTranslationKey
      if (alternativeTranslationCode) {
        text = T(alternativeTranslationCode, replacements)
      } else {
        text = T(processType, replacements)
      }
    }
    if (hasTrackedSnack) {
      res.trackedSnackId = SnackTrackedAdd(
        text,
        snack.type,
        id,
        snack.cancelable,
        async () => {
          let confirmText = T("SYSRowFnCancelConfirm", [tableFn.fnName])
          let confirmed = await DialogOkCancel(confirmText)
          if (confirmed !== 1) return

          res.cancelled = true
          let cancelText = T("SYSRowFnCancelling")
          let idToUse = res.trackedSnackId
            .replaceAll(".", "_")
            .replaceAll(",", "_")
          if (!idToUse.startsWith("ovlsnack")) {
            idToUse = "ovlsnack" + idToUse
          }
          let snackEl = document.getElementById(idToUse)
          if (snackEl) {
            let cancelIcon = snackEl.querySelector(".ovl-snack-cancel")
            if (cancelIcon) cancelIcon.remove()

            let contentEl = document.getElementById(idToUse + "content")
            if (contentEl) {
              contentEl.innerHTML += " - " + cancelText
            }
          }
          ovl.getActions().ovl.internal._CancelRowFn({
            fnKey: tableFn.fnId,
            tableDefId: tableFn.tableDefId,
            clientId: state.ovl.app.clientId,
          })
        }
      )
    } else {
      SnackAdd(text, snack.type, snack.fireAndForgetSnackDuration)
    }
  }

  if (executionHookFn) {
    executionHookFn(hookTableRowFnResultExecution)
  }

  return res
}

export const _LongRunningProcessEnd: SYSAction<{
  processType: SYSLongRunningProcess
  instance?: string
}> = (value, { global }) => {
  let state = global.getState()
  let actions = global.getActions()
  let processState = state.ovl.longRunningProcessTracker[value.processType]
  if (processState) {
    let instance = value.instance
    if (!instance) {
      instance = value.processType
    }
    let instanceState = processState["instance"][instance]

    if (instanceState && instanceState.isRunning) {
      if (instanceState.hasTrackedSnack) {
        SnackTrackedRemove(instanceState.id)
      }
      if (instanceState.lockedDialog) {
        actions.ovl.dialog._DialogSetLock({
          dialog: instanceState.lockedDialog,
          locked: false,
          instance: instanceState.lockedDialogInstance,
        })
      }
      if (instanceState.lockedScreen) {
        actions.ovl.screen._ScreenSetLock({
          screen: instanceState.lockedScreen,
          locked: false,
          instance: instanceState.lockedScreenInstance,
        })
      }

      if (instanceState.lockedFormType) {
        let allFormState = state.ovl.forms
        instanceState.lockedFormType.forEach((f) => {
          if (allFormState[f.formType] && allFormState[f.formType][f.formId]) {
            allFormState[f.formType][f.formId].locked = false
          }
        })
      }
      instanceState.refCounter--

      if (instanceState.refCounter < 1) {
        instanceState.isRunning = false
      }
    }
  }
}
